import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageIntro from "@/components/PageIntro";
import { Reveal } from "@/lib/motion";

function Block({ eyebrow, title, children, testId }) {
  return (
    <Reveal>
      <div data-testid={testId} className="grid grid-cols-1 gap-6 border-b border-white/10 py-14 lg:grid-cols-[1fr_1.6fr] lg:gap-20 lg:py-16">
        <div>
          {eyebrow && <p className="mb-3 text-xs uppercase tracking-[0.25em] text-[#8B5CF6]">{eyebrow}</p>}
          <h2 className="font-display text-2xl font-semibold text-white sm:text-3xl">{title}</h2>
        </div>
        <div className="space-y-5 text-base leading-relaxed text-white/65 md:text-lg">{children}</div>
      </div>
    </Reveal>
  );
}

export default function UeberUns() {
  return (
    <div data-testid="ueber-uns-page">
      <PageIntro eyebrow="Über uns" title="Über uns" lead="Die Geschichte hinter FestivalPlan." />

      <section className="mx-auto max-w-6xl px-5 lg:px-8">
        <Block eyebrow="Wie alles begann" title="Die Geschichte hinter FestivalPlan" testId="about-story">
          <p>
            Ich gehe gerne gemeinsam mit meiner Frau auf Festivals. Dabei habe ich immer wieder nach
            einer passenden App gesucht, die möglichst viele Dinge rund um die gemeinsame
            Festivalplanung an einem Ort vereint.
          </p>
          <p>
            Doch irgendwie habe ich nie eine App gefunden, die mir wirklich gefallen hat und genau
            das bietet, was ich mir vorgestellt habe.
          </p>
          <p>Aus dieser persönlichen Erfahrung entstand irgendwann die Idee zu FestivalPlan.</p>
          <p>
            Mein Interesse an Technik und die immer größer werdenden Möglichkeiten von
            KI-Assistenten haben schließlich dafür gesorgt, dass aus dieser Idee tatsächlich ein
            echtes Projekt werden konnte.
          </p>
          <p>
            Was zunächst mit viel Ausprobieren, Experimentieren und vermutlich dem einen oder
            anderen grauen Haar begonnen hat, ist mittlerweile zu einer funktionierenden und
            nutzbaren App geworden.
          </p>
        </Block>

        <Reveal>
          <div data-testid="about-motto" className="border-b border-white/10 py-16 lg:py-20">
            <p className="font-display text-3xl font-bold tracking-tighter text-gradient sm:text-4xl lg:text-5xl">
              Made from the Crew for the Crew.
            </p>
            <p className="mt-3 text-base uppercase tracking-[0.2em] text-white/60">Von Festivalgängern für Festivalgänger.</p>
            <div className="mt-8 max-w-2xl space-y-5 text-base leading-relaxed text-white/65 md:text-lg">
              <p>
                FestivalPlan ist aus einem echten Bedarf heraus entstanden. Die Idee war nicht,
                einfach noch eine weitere App zu entwickeln, sondern eine Lösung zu schaffen, die die
                gemeinsame Planung vor einem Festival einfacher und übersichtlicher macht.
              </p>
              <p>
                Von der Planung der Crew über Aufgaben, Listen und Fahrgemeinschaften bis hin zu
                weiteren praktischen Funktionen soll möglichst vieles an einem Ort zusammenkommen.
              </p>
            </div>
          </div>
        </Reveal>

        <Block eyebrow="Der Weg" title="Von der Idee zur App" testId="about-journey">
          <p>
            Aus viel Rumprobieren, unzähligen Änderungen und dem einen oder anderen grauen Haar ist
            langsam etwas entstanden, das tatsächlich funktioniert.
          </p>
          <p>
            FestivalPlan ist mittlerweile so weit fertig, dass die App von echten Festivalgängern
            genutzt und getestet werden kann.
          </p>
          <p>Trotzdem ist die Entwicklung damit nicht abgeschlossen.</p>
        </Block>

        <Block eyebrow="Ausblick" title="FestivalPlan wird weiterentwickelt" testId="about-future">
          <p>
            Wir arbeiten ständig daran, FestivalPlan weiter zu verbessern und neue Funktionen und
            Ideen einzubauen.
          </p>
          <p>Dabei geht es uns nicht darum, möglichst viele Funktionen anzubieten.</p>
          <p>
            Viel wichtiger ist, dass die Funktionen im echten Festival-Alltag einen sinnvollen
            Nutzen haben und die Planung mit der eigenen Crew tatsächlich einfacher machen.
          </p>
          <p>
            Deshalb freuen wir uns auch über{" "}
            <Link to="/support" data-testid="about-feedback-link" className="text-[#00E5FF] underline-offset-4 hover:underline">
              Feedback und Ideen
            </Link>{" "}
            aus der Community. Denn am Ende soll FestivalPlan genau das bleiben, wofür die App
            ursprünglich entstanden ist: eine App von Festivalgängern für Festivalgänger.
          </p>
        </Block>

        <Reveal>
          <div data-testid="about-closing" className="py-16 text-center lg:py-24">
            <p className="font-display text-3xl font-bold tracking-tighter text-white sm:text-4xl">
              Made from the Crew for the Crew.
            </p>
            <p className="mt-3 text-base text-white/65 md:text-lg">Von Festivalgängern für Festivalgänger. 🫶🏼</p>
            <p className="mt-6 text-sm uppercase tracking-[0.25em] text-[#8B5CF6]">Deine Crew. Dein Festival. FestivalPlan.</p>
            <Link to="/download" data-testid="about-download-link" className="btn-primary group mt-10 inline-flex items-center gap-2 px-6 py-3 text-sm font-medium">
              FestivalPlan laden
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
